import React, { useState, useRef, useEffect, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import useWindowSize from 'shared/hooks/useWindowResize';
import breakpoints from 'theme/atoms/breakpoints';
import { ExtractionClass } from 'shiftNotes/_types';
import {
  getConfigForExtractionClass,
  formatCategoryLabel,
} from 'shiftNotes/helpers/entityTagUtils';

import * as Styled from 'shiftNotes/styles/EntityTag.style';
import EntityTagPopover from './EntityTagPopover';

export interface EntityTagColors {
  bg: string;
  border: string;
  text: string;
}

export interface EntityTagProps {
  extractionClass: ExtractionClass;
  category: string;
  description?: string | null;
  colors?: EntityTagColors;
}

export default function EntityTag({
  extractionClass,
  category,
  description,
  colors,
}: EntityTagProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [windowWidth] = useWindowSize();
  const isDesktopView = windowWidth >= parseInt(breakpoints.tablet, 10);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tagRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation('pageShiftNotes');
  const { icon: Icon } = getConfigForExtractionClass(extractionClass);
  const label = formatCategoryLabel(category);
  const normalizedKey = extractionClass.toLowerCase().replace(/\s+/g, '_');
  const classLabel = t(`extractionClass.${normalizedKey}`, {
    defaultValue: extractionClass,
  });
  const popoverTitle =
    normalizedKey === 'urgency'
      ? `${classLabel}: ${formatCategoryLabel(category)}`
      : classLabel;

  const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
    if (isDesktopView) {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
        leaveTimeoutRef.current = null;
      }
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMouseLeave = () => {
    if (isDesktopView) {
      leaveTimeoutRef.current = setTimeout(() => {
        setAnchorEl(null);
      }, 100);
    }
  };

  const handlePopoverMouseEnter = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  };

  const handlePopoverMouseLeave = () => {
    if (isDesktopView) {
      setAnchorEl(null);
    }
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (!isDesktopView) {
      if (anchorEl) {
        setAnchorEl(null);
      } else {
        setAnchorEl(event.currentTarget);
      }
    }
  };

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open || isDesktopView) return undefined;

    const handleDocumentClick = (event: Event) => {
      const target = event.target as Node;
      const isOutsideTag = !tagRef.current || !tagRef.current.contains(target);
      const isOutsidePopover =
        !popoverRef.current || !popoverRef.current.contains(target);
      if (isOutsideTag && isOutsidePopover) {
        setAnchorEl(null);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [open, isDesktopView]);

  return (
    <>
      <Styled.Tag
        ref={tagRef}
        data-testid="entity-tag"
        $hasDescription={!!description}
        $isActive={open}
        $bg={colors?.bg}
        $borderColor={colors?.border}
        $hoverBg={colors?.border}
        onMouseEnter={description ? handleMouseEnter : undefined}
        onMouseLeave={description ? handleMouseLeave : undefined}
        onClick={description ? handleClick : undefined}
      >
        <Styled.TagIcon $stroke={colors?.text}>
          <Icon size={18} />
        </Styled.TagIcon>
        <Styled.TagLabel $color={colors?.text}>{label}</Styled.TagLabel>
      </Styled.Tag>
      {description && (
        <EntityTagPopover
          popoverRef={popoverRef}
          title={popoverTitle}
          description={description}
          anchorEl={anchorEl}
          open={open}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        />
      )}
    </>
  );
}
